import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook for managing preview URLs for files/images
 * Handles cleanup of object URLs to prevent memory leaks
 */
export function useFilePreview() {
  const previewUrlsRef = useRef<Map<string, string>>(new Map());

  // Store preview URL for an item
  const setPreview = useCallback((id: string, url: string) => {
    previewUrlsRef.current.set(id, url);
  }, []);

  // Get preview URL for an item
  const getPreview = useCallback((id: string): string | undefined => {
    return previewUrlsRef.current.get(id);
  }, []);

  // Remove preview URL and revoke it
  const removePreview = useCallback((id: string) => {
    const url = previewUrlsRef.current.get(id);

    if (url) {
      URL.revokeObjectURL(url);
      previewUrlsRef.current.delete(id);
    }
  }, []);

  // Cleanup all preview URLs on unmount
  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      previewUrlsRef.current.clear();
    };
  }, []);

  return {
    setPreview,
    getPreview,
    removePreview,
    previewUrlsRef
  };
}
